import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ClientSideRowModelModule, ValidationModule } from "ag-grid-community";
import { CancelMandateButton, DirectDebitDetailsStyled } from "./styled";
import {
  CustomBreadcrumb,
  CurrencyLabel,
  InfoLabel,
  InfoValue,
  StatusBadge,
  ConfirmationModal,
  SnackMsg,
} from "../../components";
import {
  COMMON_PAYMENT_CONSTANTS,
  DIRECT_DEBIT_CONSTANTS,
  ADMIN_TYPE,
} from "../../config/constants";
import { MANDATE_STATUS } from "./constants";
import { ROUTES } from "../../config/routes";
import { SKIPLY_MERCHANT_ID } from "../../utils/Config";
import { Skeleton } from "@mui/material";
import dayjs from "dayjs";
import { API_URL, GRID_COLUMN_EMPTY_ICONS } from "utils/Constants";
import { ApiService } from "utils/ApiService";

const DirectDebitDetails = () => {
  const { mandateId } = useParams();
  const navigate = useNavigate();
  const [mandateData, setMandateData] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelErrorMsg, setCancelErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const merchantIdGlobal = useMemo(() => {
    const role = localStorage.getItem("role");
    if (role === ADMIN_TYPE.SUPER_ADMIN)
      return localStorage.getItem("globalMerchantId") || "";
    if (role === ADMIN_TYPE.SCHOOL_ADMIN)
      return localStorage.getItem("schoolAdminMerchantId") || "";
    return "";
  }, []);

  const isSuperAdmin = useMemo(
    () =>
      localStorage.getItem("role") === ADMIN_TYPE.SUPER_ADMIN &&
      merchantIdGlobal === SKIPLY_MERCHANT_ID,
    [merchantIdGlobal],
  );

  const fetchMandateDetail = useCallback(async () => {
    setIsLoading(true);
    try {
      const storedMandate = sessionStorage.getItem("selectedDirectDebit");
      if (storedMandate) {
        const parsed = JSON.parse(storedMandate);
        if (String(parsed.id) === String(mandateId)) {
          setMandateData(parsed);
          return;
        }
      }
      sessionStorage.setItem(
        "flashMsg",
        "Session expired. Please select a direct debit mandate from the list.",
      );
      navigate(ROUTES.DIRECT_DEBIT, { replace: true });
    } catch (err) {
      navigate(ROUTES.DIRECT_DEBIT, { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [mandateId, navigate]);

  useEffect(() => {
    fetchMandateDetail();
  }, [fetchMandateDetail]);

  const columnDefs = useMemo(
    () => [
      {
        field: "title",
        headerName: COMMON_PAYMENT_CONSTANTS.PAYMENT_MONTH,
        valueFormatter: (params) => {
          const title = params.data?.title;
          const description = params.data?.description;
          return title || description
            ? [title, description].filter(Boolean).join(" ")
            : "N/A";
        },
        resizable: true,
        sortable: true,
        unSortIcon: true,
        icons: GRID_COLUMN_EMPTY_ICONS,
        filter: false,
        flex: 1,
        minWidth: 150,
      },
      {
        field: "dueDate",
        headerName: COMMON_PAYMENT_CONSTANTS.PAYMENT_DUE_DATE,
        valueFormatter: (params) => {
          return dateFormatter(params.value);
        },
        flex: 1,
        minWidth: 120,
      },
      {
        field: "paymentInfo.ftrNumber",
        headerName: DIRECT_DEBIT_CONSTANTS.PAYMENT_FTR_NUMBER,
        flex: 1,
        minWidth: 180,
        valueFormatter: (params) => {
          return params.data?.paymentInfo?.ftrNumber ?? "";
        },
      },
      {
        field: "amount",
        headerName: COMMON_PAYMENT_CONSTANTS.PAYMENT_AMOUNT,
        flex: 1,
        minWidth: 100,
        cellRenderer: (params) => <CurrencyLabel amount={params.value ?? 0} />,
      },
      {
        field: "status",
        headerName: COMMON_PAYMENT_CONSTANTS.PAYMENT_STATUS,
        flex: 1,
        minWidth: 100,
        cellRenderer: (params) => <StatusBadge status={params.value} />,
      },
      {
        field: "paymentInfo.rrn",
        headerName: DIRECT_DEBIT_CONSTANTS.PAYMENT_RRN,
        flex: 1,
        minWidth: 140,
        valueFormatter: (params) => {
          return params.data?.paymentInfo?.rrn ?? "";
        },
      },
      {
        field: "paymentInfo.failureReason",
        headerName: COMMON_PAYMENT_CONSTANTS.PAYMENT_FAILED_REASON,
        flex: 1,
        minWidth: 230,
        valueFormatter: (params) => {
          return params.data?.failureReason ?? "";
        },
      },
    ],
    [isSuperAdmin],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      resizable: false,
      suppressMovable: true,
    }),
    [],
  );

  const handleCancelClick = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = async () => {
    setIsCancelling(true);
    try {
      const cancelUrl = `${API_URL.CANCEL_DIRECT_DEBIT}/${mandateData.instructionId}`;
      const resp = await ApiService.putRequest(cancelUrl, {
        merchantId: merchantIdGlobal,
      });

      if (resp && resp.status && resp.status.code === "Y") {
        setShowCancelModal(false);
        const successMsg =
          resp.status.message || "Direct debit mandate cancelled successfully";
        sessionStorage.setItem("flashMsg", successMsg);
        navigate(ROUTES.DIRECT_DEBIT, { replace: true });
      } else {
        const errorMsg =
          (resp && resp.status && resp.status.message) ||
          "Failed to cancel direct debit mandate. Please try again.";
        setCancelErrorMsg(errorMsg);
      }
    } catch (error) {
      setCancelErrorMsg(
        error?.message ||
          "Failed to cancel direct debit mandate. Please try again.",
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const dateFormatter = (date) => {
    if (!date) return "";
    const d = dayjs(date);
    return d.isValid() ? d.format("DD/MM/YYYY") : "";
  };

  if (isLoading) {
    return (
      <CustomBreadcrumb
        header="Direct debits / Loading..."
        currentPage="Loading Details..."
        isFromLandingPage={false}
        showBackButton={true}
        onBackClick={() => navigate(-1)}
        parentPage={DIRECT_DEBIT_CONSTANTS.BREADCRUMB_DIRECT_DEBIT}
        parentPageHref={ROUTES.DIRECT_DEBIT}
      >
        <DirectDebitDetailsStyled>
          {/* Skeleton for combined section */}
          <div className="combined-section">
            <div className="section-part">
              <Skeleton
                variant="rectangular"
                width={150}
                height={24}
                style={{ marginBottom: "16px" }}
              />
              <Skeleton variant="rectangular" width="100%" height={200} />
            </div>
            <div className="section-part">
              <Skeleton
                variant="rectangular"
                width={150}
                height={24}
                style={{ marginBottom: "16px" }}
              />
              <Skeleton variant="rectangular" width="100%" height={150} />
            </div>
          </div>

          {/* Skeleton for payment section */}
          <div className="payment-section">
            <Skeleton
              variant="rectangular"
              width={200}
              height={24}
              style={{ marginBottom: "16px" }}
            />
            <Skeleton variant="rectangular" width="100%" height={400} />
          </div>
        </DirectDebitDetailsStyled>
      </CustomBreadcrumb>
    );
  }

  if (!mandateData) return null;

  const canCancelPlan =
    mandateData?.status?.toUpperCase() === MANDATE_STATUS.ACTIVE &&
    !isCancelling;

  const gradeValue = mandateData?.beneficiary?.grade
    ? ` / ${mandateData.beneficiary.grade}`
    : "";
  const breadcrumbLabel = `Mandate ${gradeValue}`;

  return (
    <CustomBreadcrumb
      header={breadcrumbLabel}
      currentPage={breadcrumbLabel}
      isFromLandingPage={false}
      showBackButton={true}
      onBackClick={() => navigate(-1)}
      parentPage={DIRECT_DEBIT_CONSTANTS.BREADCRUMB_DIRECT_DEBIT}
      parentPageHref={ROUTES.DIRECT_DEBIT}
      rightContent={
        canCancelPlan ? (
          <CancelMandateButton
            onClick={handleCancelClick}
            bsPrefix="cancel-plan-btn"
          >
            Cancel mandate
          </CancelMandateButton>
        ) : null
      }
    >
      <DirectDebitDetailsStyled>
        {/* Mandate details */}
        <div className="combined-section">
          {/* Mandate details */}
          <div className="section-part">
            <div className="section-header">
              <h3 className="section-title">
                {DIRECT_DEBIT_CONSTANTS.DETAIL_MANDATE_DETAILS}
              </h3>
            </div>
            <div className="info-grid">
              <div className="info-item">
                <InfoLabel>
                  {DIRECT_DEBIT_CONSTANTS.DETAIL_DDA_REFERENCE_NO}
                </InfoLabel>
                <InfoValue>{mandateData?.ddaReferenceNumber}</InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{DIRECT_DEBIT_CONSTANTS.DETAIL_STUDENT_ID}</InfoLabel>
                <InfoValue>{mandateData?.beneficiary?.studentId}</InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>
                  {DIRECT_DEBIT_CONSTANTS.DETAIL_MANDATE_STATUS}
                </InfoLabel>
                <InfoValue>
                  <StatusBadge
                    status={mandateData?.mandateInfo?.mandateStatus}
                  />
                </InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{DIRECT_DEBIT_CONSTANTS.DETAIL_STATUS}</InfoLabel>
                <InfoValue>
                  <StatusBadge status={mandateData?.status} />
                </InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{DIRECT_DEBIT_CONSTANTS.DETAIL_SERVICE}</InfoLabel>
                <InfoValue>
                  {mandateData?.productDetails?.productName}
                </InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>{DIRECT_DEBIT_CONSTANTS.DETAIL_FREQUENCY}</InfoLabel>
                <InfoValue>{mandateData?.productDetails?.type}</InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>
                  {DIRECT_DEBIT_CONSTANTS.DETAIL_NEXT_PAYMENT_DATE}
                </InfoLabel>
                <InfoValue>
                  {dateFormatter(mandateData?.nextPaymentDate)}
                </InfoValue>
              </div>
              <div className="info-item">
                <InfoLabel>
                  {DIRECT_DEBIT_CONSTANTS.DETAIL_TOTAL_AMOUNT}
                </InfoLabel>
                <InfoValue>
                  <CurrencyLabel amount={mandateData?.totalAmount ?? 0} />
                </InfoValue>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction History Section - Separate */}
        {mandateData?.paymentSchedule &&
          mandateData?.paymentSchedule.length > 0 && (
            <div className="payment-section">
              <h3 className="section-title">
                {DIRECT_DEBIT_CONSTANTS.DETAIL_TRANSACTION_HISTORY}
              </h3>
              <div className="ag-grid-container">
                <div
                  className="ag-theme-material"
                  style={{ height: "600px", width: "100%" }}
                >
                  <AgGridReact
                    rowData={mandateData?.paymentSchedule}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    suppressCellFocus={true}
                    suppressPaginationPanel={true}
                    rowHeight={52}
                    headerHeight={42}
                    animateRows={false}
                    modules={[ClientSideRowModelModule, ValidationModule]}
                  />
                </div>
              </div>
            </div>
          )}

        <ConfirmationModal
          show={showCancelModal}
          onHide={() => setShowCancelModal(false)}
          onConfirm={handleCancelConfirm}
          title={DIRECT_DEBIT_CONSTANTS.CANCEL_MANDATE_TITLE}
          message={`${DIRECT_DEBIT_CONSTANTS.CANCEL_MANDATE_MESSAGE}`}
          confirmLabel={DIRECT_DEBIT_CONSTANTS.CANCEL_MANDATE_CONFIRM}
          cancelLabel={DIRECT_DEBIT_CONSTANTS.CANCEL_MANDATE_BACK}
          isLoading={isCancelling}
        />

        {cancelErrorMsg ? (
          <SnackMsg
            msg={cancelErrorMsg}
            show={true}
            onClose={() => setCancelErrorMsg("")}
          />
        ) : null}
      </DirectDebitDetailsStyled>
    </CustomBreadcrumb>
  );
};

export default DirectDebitDetails;
